import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Tooltip, TextField, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
  Card, CardContent, Grid2 as Grid, Alert,
} from '@mui/material';
import {
  Link as LinkIcon, ContentCopy, Delete, Visibility, Refresh,
  TrendingUp, Payment, People, OpenInNew,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface PagaMeLink {
  id: string;
  code: string;
  user_id: number;
  descricao: string;
  valor: number | null;
  moeda: string;
  tipo: string;
  total_pagamentos: number;
  total_recebido: number;
  estado: string;
  criador_nome: string;
  expira_em: string | null;
  created_at: string;
}

interface PagaMePayment {
  id: string;
  pagador_nome: string | null;
  pagador_email: string | null;
  pagador_telefone: string | null;
  valor: number;
  mensagem: string | null;
  estado: string;
  pago_em: string | null;
  created_at: string;
}

const PagaMe: React.FC = () => {
  const { t } = useTranslation();
  const [links, setLinks] = useState<PagaMeLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLink, setSelectedLink] = useState<PagaMeLink | null>(null);
  const [payments, setPayments] = useState<PagaMePayment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ totalLinks: 0, totalActive: 0, totalPagamentos: 0, totalRecebido: 0 });

  const loadLinks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/pagame/links');
      if (response.data.success) {
        const data = response.data.data || [];
        setLinks(data);
        setStats({
          totalLinks: data.length,
          totalActive: data.filter((l: PagaMeLink) => l.estado === 'ACTIVO').length,
          totalPagamentos: data.reduce((sum: number, l: PagaMeLink) => sum + l.total_pagamentos, 0),
          totalRecebido: data.reduce((sum: number, l: PagaMeLink) => sum + parseFloat(String(l.total_recebido || 0)), 0),
        });
      }
    } catch (err) {
      console.error('Error loading PagaMe links:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  const loadPayments = async (link: PagaMeLink) => {
    setSelectedLink(link);
    setPaymentsLoading(true);
    try {
      const response = await api.get(`/admin/pagame/links/${link.id}/payments`);
      if (response.data.success) {
        setPayments(response.data.data || []);
      }
    } catch (err) {
      console.error('Error loading payments:', err);
    } finally {
      setPaymentsLoading(false);
    }
  };

  const handleCopy = (code: string) => {
    const url = `${window.location.origin}/pagame/${code}`;
    navigator.clipboard.writeText(url);
  };

  const formatCurrency = (value: number) =>
    `${Math.round(value || 0).toLocaleString('pt-PT')}$`;

  const formatDate = (date: string) =>
    new Date(date).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde',
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });

  const getEstadoChip = (estado: string) => {
    const colors: Record<string, 'success' | 'info' | 'warning' | 'error' | 'default'> = {
      ACTIVO: 'success', PAGO: 'info', EXPIRADO: 'warning', CANCELADO: 'error',
    };
    return <Chip label={estado} color={colors[estado] || 'default'} size="small" />;
  };

  const filteredLinks = links.filter(l =>
    l.descricao.toLowerCase().includes(search.toLowerCase()) ||
    l.criador_nome?.toLowerCase().includes(search.toLowerCase()) ||
    l.code.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold">PagaMe</Typography>
          <Typography variant="body2" color="text.secondary">
            Links de pagamento criados pelos utilizadores
          </Typography>
        </Box>
        <Button startIcon={<Refresh />} onClick={loadLinks} variant="outlined">
          Atualizar
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Total Links</Typography>
              <Typography variant="h4" fontWeight="bold">{stats.totalLinks}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Links Activos</Typography>
              <Typography variant="h4" fontWeight="bold" color="success.main">{stats.totalActive}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Pagamentos</Typography>
              <Typography variant="h4" fontWeight="bold" color="info.main">{stats.totalPagamentos}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="body2" color="text.secondary">Total Recebido</Typography>
              <Typography variant="h4" fontWeight="bold" color="primary.main">
                {formatCurrency(stats.totalRecebido)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Pesquisar por descrição, criador ou código..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2 }}
        size="small"
      />

      {/* Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : filteredLinks.length === 0 ? (
        <Alert severity="info">Nenhum link PagaMe encontrado</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Código</TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Criador</TableCell>
                <TableCell align="right">Valor</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="center">Pagamentos</TableCell>
                <TableCell align="right">Recebido</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Criado</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLinks.map((link) => (
                <TableRow key={link.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                      {link.code}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                      {link.descricao}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{link.criador_nome}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    {link.valor ? formatCurrency(link.valor) : (
                      <Chip label="Aberto" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={link.tipo === 'UNICO' ? 'Único' : 'Múltiplo'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">{link.total_pagamentos}</TableCell>
                  <TableCell align="right">
                    {parseFloat(String(link.total_recebido || 0)) > 0
                      ? formatCurrency(parseFloat(String(link.total_recebido)))
                      : '-'}
                  </TableCell>
                  <TableCell>{getEstadoChip(link.estado)}</TableCell>
                  <TableCell>
                    <Typography variant="caption">{formatDate(link.created_at)}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Ver pagamentos">
                      <IconButton size="small" onClick={() => loadPayments(link)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copiar link">
                      <IconButton size="small" onClick={() => handleCopy(link.code)}>
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Payments Dialog */}
      <Dialog open={!!selectedLink} onClose={() => setSelectedLink(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Pagamentos — {selectedLink?.descricao}
          <Typography variant="body2" color="text.secondary">
            Código: {selectedLink?.code} | Criador: {selectedLink?.criador_nome}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {paymentsLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : payments.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>Nenhum pagamento recebido neste link</Alert>
          ) : (
            <TableContainer sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Pagador</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Telefone</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell>Mensagem</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Data</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>{p.pagador_nome || '-'}</TableCell>
                      <TableCell>{p.pagador_email || '-'}</TableCell>
                      <TableCell>{p.pagador_telefone || '-'}</TableCell>
                      <TableCell align="right">{formatCurrency(p.valor)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                          {p.mensagem || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={p.estado}
                          color={p.estado === 'PAID' ? 'success' : p.estado === 'PENDING' ? 'warning' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {p.pago_em ? formatDate(p.pago_em) : formatDate(p.created_at)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedLink(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PagaMe;
